import MapsService, {
	FieldType,
	Filter, IconType,
	LocationType,
	MapProviderType,
	OperatorType,
	SpatialRelation
} from "@churchofjesuschrist/maps-service";
import {cloneOverlay, getOverlayBounds} from "@churchofjesuschrist/maps-google-overlays";

export default function MapService($window, $document, $q, env, UserService, google, utils, DataConst) {
	"ngInject";

	let apiDefer = null,
		mapsSave = null;

	//TODO: update colors from MapsService.getLayers()
	const metadata = {
		MISSION: {
			layer: LocationType.MISSION,
			color: "#F68D2E",
			width: 5,
			rank: 1
		},
		ZONE: {
			layer: LocationType.TEACHING_ZONE,
			color: "#6DB344",
			width: 4,
			rank: 2
		},
		DISTRICT: {
			layer: LocationType.TEACHING_DISTRICT,
			color: "#49CCE6",
			width: 4,
			rank: 3
		},
		AREA: {
			layer: LocationType.TEACHING_AREA,
			color: "#BD0057",
			width: 3,
			rank: 4
		},
		WARD: {
			layer: LocationType.WARD,
			color: "#DC3838",
			width: 2,
			rank: 5
		},
		WARD_OVERLAY: {
			layer: LocationType.WARDS__OTHERS,
			layerPrefix: LocationType.WARD + "__",
			color: "#FDA1B2",
			width: 4,
			rank: 6
		},
		STAKE: {
			layer: LocationType.STAKE,
			color: "#93C742",
			width: 3,
			rank: 7
		},
		STAKE_OVERLAY: {
			layer: LocationType.STAKES__OTHERS,
			layerPrefix: LocationType.STAKE + "__",
			color: "#D3E952",
			width: 5,
			rank: 8
		},
		VISITORS_CENTER: {
			layer: LocationType.VISITORS_CENTER,
			color: "#974A07",
			rank: 9
		},
		HISTORIC_SITE: {
			layer: LocationType.HISTORIC_SITE,
			color: "#B00504",
			rank: 10,
			onTheFlyLoaded: false
		},
		MEETINGHOUSE: {
			layer: LocationType.MEETINGHOUSE,
			color: "#5d3c04",
			rank: 11
		},
		TEMPLE: {
			layer: LocationType.TEMPLE,
			color: "#e3b157",
			rank: 12
		},
		CUSTOM: {
			layer: LocationType.CUSTOM__MISSION,
			color: "#8F4200",
			width: 5,
			rank: 13
		}
	};

	MapsService.configure({
		proxy: `${env.apiUrl}/proxy`
	});

	const MapService = {
		loadMapsApi: () => {
			if (apiDefer === null) {
				apiDefer = $q.defer();

				UserService.getSettings().then((settings) => {
					MapsService.configure({
						client: settings.mapsClient
					});

					mapsSave = settings.mapsSave;
				});

				MapsService.loadMaps(MapProviderType.GOOGLE, {libraries: ["geometry"]}).then(({api}) => {
					apiDefer.resolve(api)
				});
			}
			return apiDefer.promise;
		},

		getDetails: (id, layer) => {
			return MapsService.getLocation(id, layer);
		},
		getBoundary: (id, layer) =>{
			return MapsService.getLocationBoundary(id, layer, {generalize: false});
		},
		getBounds: (id, layer) => {
			return MapsService.getLocationExtent(id, layer);
		},
		getCentroids: (id, layer) => {
			//TODO: CentroidType.INTERNAL
			return MapsService.getLocationExtent(id, layer);
		},

		merge: (polygons) => {
			let d = $q.defer();
			// if 1 polygon, skip merge
			switch (polygons.length) {
				case 0:
					d.resolve(null);
					break;
				case 1:
					d.resolve(cloneOverlay(polygons[0]));
					break;
				default:
					MapsService.union(polygons).then((results)=>{
						d.resolve(results[0]);
					});
					break;
			}
			return d.promise;
		},
		split: (polygons, split) => {
			return MapsService.cut(polygons, split);
		},
		adjust: (polygons, adjust) => {
			return MapsService.adjustTopology(polygons, adjust, null, {tolerance: {minimumArea: 5000}});
		},
		generateLayer: (polygons, border) => {
			return MapsService.createTopology(polygons, border);
		},
		shapesRelate: (shapes1, shapes2, relation = SpatialRelation.INTERIOR_INTERSECTS) => {
			if (shapes1.length && shapes2.length) {
				return MapsService.relation(shapes1, shapes2, {relation});
			} else {
				return new Promise((resolve) => resolve([]));
			}
		},
		centroid: (polygons) => {
			return MapsService.centroids(polygons).then((centroids) => {
				return centroids ? centroids[0].getPosition() : null;
			});
		},

		centerMap: (org, map) => {
			setTimeout(() => {
				let bounds = utils.getOrgBounds(org);
				if (bounds && map) {
					map.fitBounds(bounds);
				}
			});
		},
		getIdentity: (org) => (
			{id: org.id, type: MapService.getLayer(org.type)}
		),
		serializeIdentity: (org) => {
			let layer = MapService.getLayer(org.type);
			return layer ? `${layer}:${org.id}` : org.id;
		},
		getLayer: (type) => {
			let metadatum = metadata[type];
			return metadatum ? metadatum.layer : null;
		},
		getColor: (type) => {
			let metadatum = metadata[type];
			return metadatum ? metadatum.color : null;
		},
		getIconUrl: (type) => {
			let metadatum = metadata[type];
			if (metadatum) {
				let iconType = null;
				if (DataConst.FACILITY_UNIT_ORG_TYPES.indexOf(metadatum.layer) !== -1) {
					iconType = IconType.PIN;
				}
				return MapsService.getIconUrl({
					layer: metadatum.layer,
					legend: true,
					type: iconType,
				})
			} else {
				return null;
			}
		},
		getWidth: (type) => {
			let metadatum = metadata[type];
			return metadatum ? metadatum.width : null;
		},
		getZIndex: (type) => {
			let metadatum = metadata[type],
				max = 12,
				rank = metadatum ? metadatum.rank : max;
			return max - rank;
		},
		getType: (layer) => {
			let sublayer = layer.includes("__");
			return Object.keys(metadata).find((type) => {
				let metadatum = metadata[type];
				return sublayer && metadatum.layerPrefix ? layer.indexOf(metadatum.layerPrefix) === 0 : metadatum.layer === layer;
			});
		},
		getSubLayer: (layer) => {
			let subLayer = null;
			if (layer) {
				let parts = layer.split("__");
				subLayer = parts.length >= 2 ? parts[1].trim() : null;
			}
			return subLayer || null;
		},
		groupByType: (orgs) => {
			let groups = {};
			if (orgs) {
				orgs.forEach((org) => {
					let group = groups[org.type];
					if (!group) {
						group = groups[org.type] = [];
					}
					group.push(org);
				});
			}
			return Object.keys(groups).map((type) => (
				{type, list: groups[type]}
			)).sort(MapService.sortTypeGroups);
		},
		updateTypeGroups: (orgs, groups) => {
			let newGroups = MapService.groupByType(orgs);
			if (groups) {
				// add / update groups orgs
				newGroups.forEach((newGroup) => {
					let group = groups.filter((oldGroup) => (oldGroup.type === newGroup.type))[0];
					if (group) {
						// add group orgs
						newGroup.list.forEach((newOrg) => {
							if (!utils.containsOrg(group.list, newOrg)) {
								group.list.push(newOrg);
							}
						});

						// remove group orgs
						utils.cloneArray(group.list).forEach((oldOrg) => {
							if (!utils.containsOrg(newGroup.list, oldOrg)) {
								utils.removeItem(oldOrg, group.list);
							}
						});

						group.list.sort(utils.sortOrgs);
					} else {
						// add group
						groups.push(newGroup);
					}
				});

				// remove groups
				utils.cloneArray(groups).forEach((oldGroup) => {
					let group = newGroups.filter((newGroup) => (newGroup.type === oldGroup.type))[0];
					if (!group) {
						utils.removeItem(oldGroup, groups);
					}
				});

				groups.sort(MapService.sortTypeGroups);
			} else {
				groups = newGroups;
			}
			return groups;
		},
		sortTypeGroups: (a, b) => (
			metadata[a.type].rank - metadata[b.type].rank
		),

		getCustoms: (missionId) => {
			return MapsService.getLocations({
				layers: [metadata.CUSTOM.layer],
				filters: [Filter.create(FieldType.PARENT, OperatorType.EQUALS, missionId)],
			}).then((results) => {
				return results.map(MapService.convertToOrg);
			})
		},
		getFacilities: (missionId) => {
			return MapsService.getLocations({
				layers: [metadata.HISTORIC_SITE.layer,metadata.TEMPLE.layer,metadata.VISITORS_CENTER.layer],
				filters: [Filter.create(FieldType.ASSOCIATED, OperatorType.EQUALS, missionId)],
			}).then((results) => {
				return results.map(MapService.convertToOther);
			})
		},
		getOnTheFlyAssociated: (missionId,layer) => {
			if (!metadata[layer].onTheFlyLoaded) {
				metadata[layer].onTheFlyLoaded = true;
				return MapsService.getLocations({
					layers: [layer],
					filters: [Filter.create(FieldType.ASSOCIATED, OperatorType.EQUALS, missionId)],
				}).then((results) => {
					return results.map(MapService.convertToOther);
				})
			}
		},
		getOnTheFlyAssociatedAlreadyLoaded: (layer) => {
			return metadata[layer].onTheFlyLoaded;
		},
		createCustom: (custom, mission) => {
			let d = $q.defer();
			let location = MapService.convertToLocation(custom, mission);
			if (mapsSave) {
				MapsService.createLocation(location).then((location) => {
					let custom = MapService.convertToOrg(location);
					d.resolve(custom);
				});
			} else {
				//Generate ID
				location.id = Math.floor(Math.random() * 10000);
				custom = MapService.convertToOrg(location);
				d.resolve(custom);
			}
			return d.promise;
		},
		updateCustom: (custom, mission) => {
			let d = $q.defer();
			let location = MapService.convertToLocation(custom, mission);
			if (mapsSave) {
				MapsService.updateLocation(location).then((location) => {
					let custom = MapService.convertToOrg(location);
					d.resolve(custom);
				});
			} else {
				custom = MapService.convertToOrg(location);
				d.resolve(custom);
			}
			return d.promise;
		},
		deleteCustom: (custom) => {
			let d = $q.defer();
			let location = MapService.convertToLocation(custom);
			if (mapsSave) {
				MapsService.deleteLocation(location.id, location.type).then(() => {
					d.resolve();
				});
			} else {
				d.resolve();
			}
			return d.promise;
		},

		convertToOrg: (location) => {
			let boundary = location.boundary;
			return {
				id: location.id,
				type: MapService.getType(location.type),
				name: location.name,
				description: location.notes,
				layer: location.type,
				boundary,
				bounds: boundary ? getOverlayBounds(boundary) : null
			};
		},
		convertToLocation: (org, mission) => (
			{
				id: org.id ? org.id.toString() : null,
				type: MapService.getLayer(org.type),
				name: org.name,
				boundary: org.boundary,
				parent:  mission ? {
					type: MapService.getLayer(mission.type),
					id: mission.id.toString()
				} : null,
				notes: org.description,
			}
		),
		convertToOther: (org) => {
			let obj ={
				id: org.id ? org.id.toString() : null,
				type: MapService.getLayer(org.type),
				name: org.name,
				position: org.coordinates,
				boundary: org.boundary
			};
			return obj;
		}
	};
	return MapService;
}
